import { useInputPopup } from "@coworker/app/src/components/InputPopup";
import { useMultiselectOverflowMenu } from "@coworker/app/src/features/tasks/useMultiselectOverflowMenu";
import { useMultiSelectForTab } from "@coworker/app/src/hooks/useMultiSelect";
import { MultiselectReturnType } from "@coworker/app/src/types/multiselectReturnType";
import { useTasksCloseReasonOptions } from "@coworker/apprestructured/src/shared/hooks/tasks/useTasksCloseReasonOptions";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { SelectableTaskCardWrapper } from "@coworker/apprestructured/src/shared/wrappers/SelectableTaskCardWrapper/SelectableTaskCardWrapper";
import TaskCardV2 from "@coworker/apprestructured/src/task-management/components/TaskCard/TaskCard";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { TaskType } from "@coworker/apprestructured/src/tasks/enums/taskTypes";
import { TasksTabs } from "@coworker/apprestructured/src/TasksOverview/constants/tasksTabs";
import { TaskListItem } from "@coworker/apprestructured/src/TasksOverview/hooks/useTasksOverview";
import { Trans, useTranslation } from "@coworker/locales";
import React, { useMemo } from "react";
import { ModalContext } from "../../../layout/context/ModalContextProvider";
import { ModalType } from "../../../layout/context/modalType";
import { LoadMore } from "../../../shared/components/LoadMore/LoadMore";
import { useItemsInfoQueries } from "../../../shared/hooks/item/useItemsInfo";
import { SimpleLoadingBall } from "../../../shared/simple/SimpleLoading/SimpleLoading";
import { Item } from "../../../shared/types/item";
import FixaButton from "../../../shared/wrappers/FixaButton/FixaButton";
import { TaskCard as TaskCardV1 } from "../../../tasks/components/TaskCard/TaskCard";
import { useTasksOverview } from "../../hooks/useTasksOverview";
import { ActivityFilterType } from "../../types/filterOptionsTypes";
import { FullTask } from "../../types/FullTask";
import styles from "./multi-select.module.css";
import { SelectedTasks } from "./SelectedTasks";

interface MultiSelectPanelProps {
  filters: ActivityFilterType;
  onShowTaskDetails: (taskId: string, taskType: TaskType) => void;
  title: string;
}

type MultiSelectTab = "open" | "inProgress" | "completed" | "closed";

const multiSelectTabMap: Record<TasksTabs, MultiSelectTab> = {
  [TasksTabs.OPEN]: "open",
  [TasksTabs.IN_PROGRESS]: "inProgress",
  [TasksTabs.UPCOMING]: "open",
  [TasksTabs.COMPLETED]: "completed",
  [TasksTabs.CLOSED]: "closed",
};

export const MultiSelectPanel = ({
  filters,
  onShowTaskDetails,
  title,
}: MultiSelectPanelProps) => {
  const { t } = useTranslation();
  const { modalState, toggleModal } = React.useContext(ModalContext);
  const tab = (modalState.props?.type?.type as TasksTabs) || TasksTabs.OPEN;

  const activeTab = multiSelectTabMap[tab];

  const tasksQueryResult = useTasksOverview(
    filters,
    tab,
    modalState.isMultiSelectTasksModalOpen
  );

  const tasks = useMemo(() => {
    return tasksQueryResult?.data?.pages.map((page) => page.list).flat() || [];
  }, [tasksQueryResult?.data?.pages]);

  const itemNos = tasks
    .filter(({ type }) => type === "v1")
    .map(({ task_data }) => (task_data as FullTask).product_article_id)
    .filter(Boolean);
  const itemsInfoResult = useItemsInfoQueries(itemNos);

  const itemsInfo = itemsInfoResult
    .map((result) => result.data)
    .filter(Boolean);

  const { selectedTasksCount, selectedTaskIds, toggle, reset } =
    useMultiSelectForTab(activeTab) as MultiselectReturnType;

  const { selectedV1TaskIds, selectedV2TaskIds, selectedV2TaskTypeIds } =
    React.useMemo(() => {
      const v1Ids: string[] = [];
      const v2Ids: string[] = [];
      const v2TaskTypeIds: string[] = [];

      selectedTaskIds?.forEach((taskId) => {
        const task = tasks.find((t) => t.task_data.id === taskId);
        if (task?.type === "v1") {
          v1Ids.push(taskId);
        } else if (task?.type === "v2") {
          v2Ids.push(taskId);
          v2TaskTypeIds.push(task.task_data.task_type_id);
        }
      });

      return {
        selectedV1TaskIds: v1Ids,
        selectedV2TaskIds: v2Ids,
        selectedV2TaskTypeIds: [...new Set(v2TaskTypeIds)],
      };
    }, [selectedTaskIds, tasks]);

  const hasSelectedV2Tasks = selectedV2TaskTypeIds.length > 0;
  const { data: allTaskTypes } = useAllTaskTypes({
    enabled: hasSelectedV2Tasks,
  });
  const selectedV2TaskTypes = useMemo(() => {
    return (
      allTaskTypes?.filter((taskType) =>
        selectedV2TaskTypeIds.includes(taskType.id)
      ) ?? []
    );
  }, [allTaskTypes, selectedV2TaskTypeIds]);
  const { closeReasonOptions } = useTasksCloseReasonOptions(
    selectedV2TaskTypes,
    {
      enabled: hasSelectedV2Tasks,
    }
  );

  const { getInput } = useInputPopup();
  const { getTaskMenu, loading } = useMultiselectOverflowMenu(
    selectedTasksCount,
    activeTab,
    selectedV1TaskIds,
    selectedV2TaskIds,
    closeReasonOptions,
    reset
  );

  const [, setSearchParams] = useSearchParamState();

  const handleTaskV2CardClick = React.useCallback(
    (taskId: string) => {
      setSearchParams((params) => {
        params.set("taskViewer", String(taskId));
      });
    },
    [setSearchParams]
  );

  const handleCheckboxSelect = React.useCallback(
    (taskId: string) => {
      if (toggle) toggle(taskId);
    },
    [toggle]
  );

  const warningPopup = React.useCallback(async () => {
    const response = await getInput("confirmation", {
      question: <Trans count={selectedTasksCount}>aboutToChangeString</Trans>,
      description: (
        <>
          <Trans>undoNotPossibleString</Trans>
          <span>
            <Trans>sureToContinueString</Trans>
          </span>
        </>
      ),
      positiveText: <Trans>yesString</Trans>,
      negativeText: <Trans>negativeString</Trans>,
    });
    if (response) await getTaskMenu();
  }, [getInput, selectedTasksCount, getTaskMenu]);

  const handleSelectedTasks = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (selectedTasksCount > 14) {
      await warningPopup();
    } else {
      await getTaskMenu();
    }
  };

  const renderTasks = (
    tasks: TaskListItem[],
    isFetching: boolean,
    fetchNextPage: () => void,
    totalCount: number,
    hasNextPage: boolean
  ) => {
    if (!tasks.length) {
      return (
        <div className={styles["no-results"]}>
          <div className={styles["no-results-title"]}>
            <Trans>noTaskResultsString</Trans>
          </div>
          <div className={styles["no-results-subtitle"]}>
            <Trans>noTasksFoundString</Trans>
          </div>
        </div>
      );
    }

    return (
      <>
        <FixaListView id="multi-select-tasks-list" size="small">
          <FixaListViewItem title={title} emphasised inset />
        </FixaListView>
        <SelectedTasks type={activeTab} tasks={tasks} />
        {tasks.map((taskListItem) => {
          const { type, task_data: task } = taskListItem;

          if (type === "v1") {
            return (
              <TaskCardV1
                key={`v1_${task.id}`}
                task={task}
                multiSelect
                type={activeTab}
                item={
                  itemsInfo?.find(
                    (item) => item?.no === task.product_article_id
                  ) as Item
                }
                onShowTaskDetails={onShowTaskDetails}
              />
            );
          }

          if (type === "v2") {
            return (
              <SelectableTaskCardWrapper
                key={`v2_${task.id}`}
                value={task.id}
                selected={selectedTaskIds?.includes(task.id)}
                onCardClick={handleTaskV2CardClick}
                onCheckboxSelect={handleCheckboxSelect}
              >
                <TaskCardV2 task={task} />
              </SelectableTaskCardWrapper>
            );
          }

          return null;
        })}
        {hasNextPage && (
          <LoadMore
            onLoadMoreClick={fetchNextPage}
            loadedItemsCount={tasks.length}
            totalCount={totalCount}
            isFetching={isFetching}
          />
        )}
      </>
    );
  };

  return (
    <FixaModal
      visible={modalState.isMultiSelectTasksModalOpen}
      handleCloseBtn={() => toggleModal({ type: ModalType.MultiSelectTasks })}
      className={styles["filters-panel"] as string}
    >
      <FixaSheets
        labelledById="activity-filter-multi-select-modal-header"
        alignment="right"
        preserveAlignment={false}
        size="small"
        fullSize
        header={
          <FixaModalHeader
            id="activity-filter-multi-select-modal-header"
            title={t("multiSelectTasksString")}
            className={styles["header"] as string}
            floating={false}
          />
        }
        footer={
          selectedTasksCount > 0 ? (
            <FixaModalFooter className={styles["footer"] as string}>
              <FixaButton
                text={t("continue2String")}
                type="primary"
                onClick={handleSelectedTasks}
              />
            </FixaModalFooter>
          ) : null
        }
      >
        <FixaModalBody>
          {loading ? (
            <div className={styles["loading"]}>
              <SimpleLoadingBall
                text={t("loadingEllipsisString")}
                size="medium"
              />
            </div>
          ) : (
            renderTasks(
              tasks,
              tasksQueryResult.isFetching,
              tasksQueryResult.fetchNextPage,
              tasksQueryResult.data?.pages[0]?.filteredCount || 0,
              tasksQueryResult.hasNextPage ?? false
            )
          )}
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
};
